import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { Button, Spinner } from "react-bootstrap";
import { alertActions, user_profileActions } from "actions";
import DownloadIcon from "components/icons/DownloadIcon";
import { statisticsErrorMessage } from "../statisticsError";
import {
    GENERIC_ERROR,
    PREPARING_MESSAGE,
    READY_MESSAGE,
    STILL_PREPARING_MESSAGE,
    truncatedMessage
} from "./constants";
import { saveBlob } from "./saveFile";
import { waitForExport } from "./polling";

export function StatisticsDownloadButton({ userId, range, excludedWorkspaceIds, excludedOrganizationIds }) {
    const dispatch = useDispatch();
    const [downloading, setDownloading] = useState(false);
    const unmounted = useRef(false);

    useEffect(() => () => { unmounted.current = true; }, []);

    const handleDownload = async () => {
        if (downloading) return;

        setDownloading(true);
        dispatch(alertActions.success(PREPARING_MESSAGE));

        try {
            const { token } = await user_profileActions.createStatisticsExport(
                userId, range, excludedWorkspaceIds, excludedOrganizationIds
            );

            const built = await waitForExport(userId, token, {
                isAbandoned: () => unmounted.current,
                onStillWaiting: () => dispatch(alertActions.success(STILL_PREPARING_MESSAGE))
            });

            if (!built) return;

            saveBlob(await user_profileActions.downloadStatisticsExport(userId, token));

            // A capped export is still a successful download, so it warns rather
            // than errors, and replaces the ready message rather than stacking on it.
            dispatch(built.truncated
                ? alertActions.warning(truncatedMessage(built.row_count))
                : alertActions.success(READY_MESSAGE));
        } catch (error) {
            // Giving up on watching is a caveat, not a failure, so it warns the way
            // a truncated export does rather than reporting a breakage.
            if (!unmounted.current) {
                dispatch(error.stillBuilding
                    ? alertActions.warning(error.message)
                    : alertActions.error(statisticsErrorMessage(error, GENERIC_ERROR)));
            }
        } finally {
            if (!unmounted.current) setDownloading(false);
        }
    };

    return (
        <Button
            variant="outline-gray"
            className="statistics_page-download-btn btn btn-outline-gray d-flex align-items-center p-0"
            disabled={downloading}
            onClick={handleDownload}
            title={downloading ? PREPARING_MESSAGE : "Download"}
            aria-label="Download"
        >
            {downloading
                ? <Spinner animation="border" size="sm" role="status" aria-hidden="true" />
                : <DownloadIcon />}
        </Button>
    );
}

export default StatisticsDownloadButton;
